import { STAGE_LABEL } from "./pipeline";

// Single source of truth for activity-event labels, used by both the
// per-prospect timeline (ProspectDetail.jsx) and the global admin journal
// (Activity.jsx). Keep in sync with backend/activities/models.py EVENT_TYPES.
export const EVENT_LABELS = {
  // Prospects / pipeline
  prospect_created: "Prospect créé",
  form_submission_received: "Questionnaire reçu",
  prospect_assigned: "Prospect assigné",
  status_changed: "Changement de statut",
  prospect_updated: "Prospect modifié",
  prospect_deleted: "Prospect mis à la corbeille",
  prospect_restored: "Prospect restauré",
  prospect_purged: "Prospect purgé de la corbeille",
  // Tasks
  task_created: "Tâche créée",
  task_completed: "Tâche terminée",
  task_updated: "Tâche modifiée",
  task_deleted: "Tâche mise à la corbeille",
  task_restored: "Tâche restaurée",
  task_purged: "Tâche purgée de la corbeille",
  task_cancelled: "Tâche annulée",
  // Pilots
  pilot_created: "Pilote créé",
  pilot_updated: "Pilote modifié",
  pilot_activated: "Pilote activé",
  pilot_completed: "Pilote terminé",
  pilot_cancelled: "Pilote annulé",
  pilot_deleted: "Pilote prévu supprimé",
  pilot_autonomy_evaluated: "Autonomie évaluée",
  pilot_contact_sharing_updated: "Partage de coordonnées modifié",
  pilot_contact_sharing_invalidated: "Partage de coordonnées révoqué",
  pilot_member_added: "Intervenant ajouté au pilote",
  pilot_member_removed: "Intervenant retiré du pilote",
  pilot_report_created: "Compte rendu de pilote publié",
  pilot_report_updated: "Compte rendu de pilote modifié",
  pilot_report_deleted: "Compte rendu de pilote mis à la corbeille",
  pilot_report_restored: "Compte rendu de pilote restauré",
  pilot_report_purged: "Compte rendu de pilote purgé de la corbeille",
  // Onboarding / migration
  onboarding_started: "Onboarding démarré",
  migration_started: "Mise en place démarrée",
  migration_completed: "Mise en place terminée",
  // Authentication / account security
  user_login: "Connexion",
  user_login_failed: "Échec de connexion",
  user_logout: "Déconnexion",
  password_changed: "Mot de passe changé",
  password_reset_requested: "Réinitialisation demandée",
  password_reset_completed: "Mot de passe réinitialisé",
  // Users / roles
  user_created: "Utilisateur créé",
  user_role_changed: "Rôle modifié",
  user_deactivated: "Compte désactivé",
  user_reactivated: "Compte réactivé",
  role_created: "Rôle créé",
  role_updated: "Rôle modifié",
  role_deleted: "Rôle mis à la corbeille",
  // Data operations
  csv_import_started: "Import CSV démarré",
  csv_import_completed: "Import CSV terminé",
  csv_import_failed: "Échec de l'import CSV",
  google_sheet_sync_started: "Synchronisation Google Sheet démarrée",
  google_sheet_sync_completed: "Synchronisation Google Sheet terminée",
  google_sheet_sync_failed: "Échec de la synchronisation Google Sheet",
  google_sheet_config_updated: "Configuration Google Sheet modifiée",
  // Cross-cutting security
  permission_denied: "Accès refusé",
  // Settings
  setting_updated: "Paramètre modifié",
  // Collaborators and organisation
  collaborator_created: "Fiche collaborateur créée",
  collaborator_updated: "Fiche collaborateur modifiée",
  collaborator_status_changed: "Statut d'un collaborateur modifié",
  collaborator_dates_corrected: "Dates d'un collaborateur corrigées",
  collaborator_responsibility_moved: "Responsable d'un collaborateur changé (passation)",
  collaborator_handover_completed: "Passation des responsabilités effectuée",
  collaborator_reorganized: "Hiérarchie réorganisée depuis l'organigramme",
  collaborator_org_chart_exported: "Organigramme exporté en PDF",
  collaborator_archived: "Fiche collaborateur archivée",
  collaborator_unarchived: "Fiche collaborateur désarchivée",
  collaborator_user_linked: "Compte CRM rattaché à une fiche",
  collaborator_user_unlinked: "Compte CRM détaché d'une fiche",
  collaborator_duplicate_override: "Doublon possible confirmé",
  collaborator_duplicate_blocked: "Doublon probable bloqué",
  service_created: "Service créé",
  service_updated: "Service modifié",
  service_responsibility_moved: "Chef ou remplaçant d'un service changé (passation)",
  org_referential_created: "Élément d'organisation créé",
  org_referential_updated: "Élément d'organisation modifié",
  // Job functions, skills and accreditations
  skill_created: "Compétence créée",
  skill_updated: "Compétence modifiée",
  skill_level_updated: "Niveau de compétence renommé",
  job_function_created: "Fonction métier créée",
  job_function_updated: "Fonction métier modifiée",
  collaborator_skill_set: "Niveau de compétence enregistré",
  collaborator_skill_corrected: "Niveau de compétence corrigé (personne sortie)",
  accreditation_granted: "Habilitation accordée",
  accreditation_revoked: "Habilitation révoquée",
  mandate_created: "Mandat légal enregistré",
  mandate_updated: "Mandat légal corrigé",
  mandate_ended: "Mandat légal terminé",
  mandate_verified: "Mandat légal vérifié",
  mandate_unverified: "Vérification du mandat retirée",
  mandate_type_created: "Type de mandat créé",
  mandate_type_updated: "Type de mandat modifié",
  // Internal finance
  finance_expense_created: "Dépense créée",
  finance_expense_corrected: "Dépense modifiée",
  finance_expense_submitted: "Dépense soumise",
  finance_expense_generated: "Brouillon créé par un abonnement",
  finance_payment_recorded: "Paiement enregistré",
  finance_payment_cancelled: "Paiement annulé",
  finance_decision_recorded: "Décision sur des avances consignée",
  finance_reimbursement_recorded: "Remboursement enregistré",
  finance_reimbursement_cancelled: "Remboursement annulé",
  finance_subscription_created: "Abonnement créé",
  finance_subscription_updated: "Abonnement modifié",
  finance_subscription_paused: "Abonnement mis en pause",
  finance_subscription_resumed: "Abonnement repris",
  finance_subscription_ended: "Abonnement arrêté",
  finance_category_created: "Catégorie de dépense créée",
  finance_settings_updated: "Paramètres Finance modifiés",
  finance_attachment_upload_started: "Envoi d'un justificatif commencé",
  finance_attachment_uploaded: "Justificatif envoyé",
  finance_attachment_downloaded: "Justificatif consulté",
  finance_attachment_cleanup_claimed: "Nettoyage d'un justificatif lancé",
  finance_attachment_cleaned: "Justificatif inutilisé supprimé",
  private_file_upload_started: "Envoi d'un fichier commencé",
  private_file_uploaded: "Fichier envoyé",
  private_file_downloaded: "Fichier consulté",
  private_file_cleanup_claimed: "Nettoyage d'un fichier lancé",
  private_file_cleaned: "Fichier inutilisé supprimé",
  procedure_created: "Procédure créée",
  procedure_updated: "Procédure modifiée",
  procedure_draft_created: "Nouvelle version de procédure commencée",
  procedure_draft_saved: "Brouillon de procédure enregistré",
  procedure_draft_discarded: "Brouillon de procédure supprimé",
  procedure_published: "Procédure publiée",
  checklist_started: "Procédure démarrée sur une mission",
  checklist_answered: "Réponse enregistrée sur une procédure",
  checklist_completed: "Procédure terminée sur une mission",
  checklist_reopened: "Procédure rouverte",
  checklist_follow_up_linked: "Tâche de suivi reliée à une anomalie",
  report_template_created: "Modèle de compte rendu créé",
  report_template_updated: "Modèle de compte rendu modifié",
  report_template_draft_created: "Nouvelle version de modèle de compte rendu",
  report_template_draft_saved: "Brouillon de modèle de compte rendu enregistré",
  report_template_draft_discarded: "Brouillon de modèle de compte rendu supprimé",
  report_template_published: "Modèle de compte rendu publié",
  mission_report_started: "Compte rendu de mission commencé",
  mission_report_updated: "Compte rendu de mission modifié",
  mission_report_submitted: "Compte rendu de mission soumis à validation",
  mission_report_published: "Compte rendu de mission publié",
  mission_report_approved: "Compte rendu de mission validé",
  mission_report_returned: "Compte rendu de mission renvoyé pour correction",
  mission_report_reopened: "Compte rendu de mission rouvert",
  attendance_recorded: "Pointage enregistré",
  attendance_corrected: "Pointage corrigé",
  attendance_voided: "Pointage annulé",
  attendance_validated: "Temps de mission validés",
  attendance_reopened: "Temps de mission rouverts",
  attendance_exported: "Export des temps téléchargé",
  equipment_category_updated: "Catégorie de matériel modifiée",
  training_feature_created: "Fonctionnalité Jamora ajoutée",
  training_feature_updated: "Fonctionnalité Jamora modifiée",
  training_feature_retired: "Fonctionnalité Jamora retirée",
  training_feature_restored: "Fonctionnalité Jamora réactivée",
  training_section_renamed: "Rubrique du catalogue renommée",
  training_module_created: "Module de formation créé",
  training_module_updated: "Module de formation modifié",
  training_module_retired: "Module de formation désactivé",
  training_module_restored: "Module de formation réactivé",
  training_program_created: "Programme de formation créé",
  training_program_updated: "Programme de formation modifié",
  training_program_draft_created: "Nouvelle version de programme en brouillon",
  training_program_draft_saved: "Brouillon de programme enregistré",
  training_program_draft_discarded: "Brouillon de programme supprimé",
  training_program_published: "Programme de formation publié",
  training_session_created: "Session de formation préparée",
  training_session_updated: "Session de formation modifiée",
  training_session_run_updated: "Déroulé de session enregistré",
  training_session_participant_added: "Participant ajouté à une session",
  training_session_participant_updated: "Présence ou résultats enregistrés",
  training_session_participant_removed: "Participant retiré d'une session",
  training_session_closed: "Session de formation clôturée",
  customer_account_opened: "Dossier client ouvert",
  customer_phase_changed: "Phase de mise en service avancée",
  customer_activated: "Mise en service déclarée",
  customer_report_noted: "Compte rendu de mission noté au dossier client",
  automation_rule_updated: "Règle d'automatisation modifiée",
  automation_run_replayed: "Exécution d'automatisation rejouée",
  automation_switch_changed: "Moteur d'automatisation arrêté ou remis en marche",
  agent_token_created: "Jeton d'agent externe créé",
  agent_token_revoked: "Jeton d'agent externe révoqué",
  customer_status_changed: "Statut du dossier client changé",
  customer_account_updated: "Dossier client modifié",
  customer_activation_corrected: "Date de mise en service corrigée",
  customer_tenant_linked: "Pharmacie liée à son compte Jamora",
  customer_tenant_verified: "Liaison Jamora vérifiée (courriel ou téléphone concordant)",
  customer_tenant_unlinked: "Liaison Jamora fermée",
  customer_health_overridden: "Santé du dossier client corrigée",
  customer_health_override_lifted: "Correction de santé levée",
  customer_objective_created: "Objectif de suivi client ajouté",
  customer_objective_deleted: "Objectif client mis à la corbeille",
  customer_objective_restored: "Objectif client restauré",
  customer_objective_purged: "Objectif client purgé de la corbeille",
  customer_objective_updated: "Objectif de suivi client modifié",
  marketing_campaign_created: "Campagne créée",
  marketing_campaign_updated: "Campagne modifiée",
  marketing_campaign_activate: "Campagne lancée",
  marketing_campaign_end: "Campagne terminée",
  marketing_campaign_reopen: "Campagne rouverte",
  marketing_campaign_archive: "Campagne archivée",
  marketing_partner_created: "Partenaire ajouté",
  marketing_partner_updated: "Partenaire modifié",
  marketing_partner_ended: "Partenariat terminé",
  marketing_partner_resumed: "Partenariat repris",
  marketing_touch_recorded: "Origine d'une pharmacie enregistrée",
  marketing_touch_cancelled: "Origine d'une pharmacie annulée",
  marketing_preferences_set: "Préférences de communication enregistrées",
  marketing_spend_linked: "Dépense reliée à une campagne",
  marketing_spend_unlinked: "Dépense détachée d'une campagne",
  marketing_exported: "Export du marketing",
  governance_decision_created: "Décision proposée",
  governance_decision_updated: "Décision modifiée",
  governance_decision_adopt: "Décision adoptée",
  governance_decision_defer: "Décision différée",
  governance_decision_cancel: "Décision annulée",
  governance_decision_repropose: "Décision remise en discussion",
  governance_decision_superseded: "Décision remplacée",
  governance_meeting_created: "Réunion créée",
  governance_meeting_updated: "Réunion modifiée",
  governance_meeting_hold: "Réunion ouverte",
  governance_meeting_close: "Réunion close",
  governance_meeting_cancel: "Réunion annulée",
  governance_meeting_addendum: "Addendum ajouté à un compte rendu",
  governance_risk_created: "Risque consigné",
  governance_risk_updated: "Risque modifié",
  governance_risk_reviewed: "Risque revu",
  governance_risk_accept: "Risque accepté",
  governance_risk_close: "Risque clos",
  governance_risk_reopen: "Risque rouvert",
  knowledge_article_created: "Article créé",
  knowledge_article_updated: "Article modifié",
  knowledge_article_draft_saved: "Brouillon d'article enregistré",
  knowledge_article_new_draft: "Nouvelle version d'article commencée",
  knowledge_article_discard_draft: "Brouillon d'article écarté",
  knowledge_article_publish: "Article publié",
  knowledge_article_review: "Article relu",
  knowledge_article_archive: "Article archivé",
  knowledge_article_unarchive: "Article réactivé",
  governance_task_linked: "Action liée à une décision",
  governance_task_unlinked: "Action retirée d'une décision",
  governance_exported: "Export de la gouvernance",
  cockpit_exported: "Export du Cockpit",
  architecture_scan_requested: "Mise à jour de la cartographie demandée",
  architecture_scan_cancelled: "Mise à jour de la cartographie annulée",
  architecture_rolled_back: "Retour à une version de la cartographie",
  architecture_exported: "Export de la cartographie",
  weekly_summary_previewed: "Aperçu du résumé hebdomadaire",
  jamora_admin_session_opened: "Jamora App ouverte",
  jamora_admin_session_closed: "Jamora App fermée",
  jamora_admin_session_refused: "Ouverture de l'administration Jamora refusée",
  jamora_admin_viewed: "Écran de l'administration Jamora consulté",
  jamora_admin_exported: "Liste de l'administration Jamora exportée en CSV",
  jamora_demo_converted: "Demande de démo Jamora convertie en prospect",
  governance_meeting_file_added: "Fichier joint à une réunion",
  governance_meeting_file_removed: "Fichier retiré d'une réunion",
  procurement_supplier_created: "Fournisseur ajouté",
  procurement_supplier_updated: "Fournisseur modifié",
  procurement_supplier_deactivated: "Fournisseur désactivé",
  procurement_supplier_reactivated: "Fournisseur réactivé",
  procurement_asset_created: "Actif ajouté à l'inventaire",
  procurement_asset_updated: "Actif modifié",
  procurement_asset_assigned: "Actif affecté",
  procurement_asset_loaned: "Actif prêté",
  procurement_asset_returned: "Actif restitué",
  procurement_asset_transferred: "Actif transféré à une autre personne",
  procurement_asset_location_changed: "Emplacement d'un actif changé",
  procurement_asset_owner_changed: "Propriétaire d'un actif changé",
  procurement_asset_condition_changed: "État d'un actif changé",
  procurement_asset_retired: "Actif retiré de l'inventaire",
  procurement_request_created: "Demande d'achat créée",
  procurement_request_updated: "Demande d'achat modifiée",
  procurement_request_submitted: "Demande d'achat soumise",
  procurement_request_approved: "Demande d'achat approuvée",
  procurement_request_rejected: "Demande d'achat refusée",
  procurement_request_approval_cancelled: "Approbation annulée après modification",
  procurement_request_cancelled: "Demande d'achat annulée",
  procurement_request_ordered: "Achat commandé",
  procurement_request_received: "Livraison enregistrée",
  procurement_request_assets_created: "Actifs créés depuis une livraison",
  procurement_request_invoiced: "Facture d'achat saisie",
  procurement_request_expense_recorded: "Dépense d'achat enregistrée",
  procurement_request_closed: "Demande d'achat clôturée",
  procurement_quote_added: "Devis ajouté",
  procurement_quote_selected: "Devis retenu",
  procurement_quote_removed: "Devis retiré",
  finance_expense_from_purchase: "Dépense créée depuis un achat",
  procurement_inventory_opened: "Inventaire ouvert",
  procurement_inventory_closed: "Inventaire clôturé",
  procurement_inventory_cancelled: "Inventaire annulé",
  procurement_exported: "Export des achats ou des actifs",
  product_item_created: "Retour produit créé",
  product_item_updated: "Retour produit modifié",
  product_item_qualified: "Retour produit qualifié",
  product_item_status_changed: "État d'un retour produit changé",
  product_item_merged: "Retour produit fusionné dans un autre",
  product_item_unmerged: "Fusion d'un retour produit défaite",
  product_item_ticket_linked: "Ticket rattaché à un retour produit",
  product_item_ticket_unlinked: "Ticket détaché d'un retour produit",
  product_item_github_linked: "Lien GitHub ajouté à un retour produit",
  product_item_github_unlinked: "Lien GitHub retiré d'un retour produit",
  product_release_created: "Version créée",
  product_release_updated: "Version modifiée",
  product_release_item_attached: "Retour produit ajouté à une version",
  product_release_item_detached: "Retour produit retiré d'une version",
  product_release_delivered: "Version livrée",
  product_release_correction_added: "Correction ajoutée à une version",
  product_release_github_linked: "Lien GitHub ajouté à une version",
  product_release_github_unlinked: "Lien GitHub retiré d'une version",
  product_incident_created: "Incident produit ouvert",
  product_incident_updated: "Incident produit modifié",
  product_incident_status_changed: "État d'un incident produit changé",
  product_incident_entry_added: "Note ajoutée à un incident produit",
  product_incident_postmortem_saved: "Post-mortem d'incident enregistré",
  product_incident_ticket_linked: "Ticket rattaché à un incident",
  product_incident_ticket_unlinked: "Ticket détaché d'un incident",
  product_incident_item_linked: "Retour produit lié à un incident",
  product_incident_item_unlinked: "Retour produit délié d'un incident",
  product_incident_task_linked: "Action préventive liée à un incident",
  product_incident_task_unlinked: "Action préventive déliée d'un incident",
  product_exported: "Export des données produit",
  records_obligation_deleted: "Obligation mise à la corbeille",
  records_obligation_restored: "Obligation restaurée",
  records_obligation_purged: "Obligation purgée de la corbeille",
  records_constitution_item_deleted: "Pièce de constitution mise à la corbeille",
  records_constitution_item_restored: "Pièce de constitution restaurée",
  records_constitution_item_purged: "Pièce de constitution purgée de la corbeille",
  records_template_deleted: "Modèle de document mis à la corbeille",
  records_template_restored: "Modèle de document restauré",
  records_template_purged: "Modèle de document purgé de la corbeille",
  records_clause_deleted: "Clause mise à la corbeille",
  records_clause_restored: "Clause restaurée",
  records_clause_purged: "Clause purgée de la corbeille",
  finance_category_deleted: "Catégorie de dépense mise à la corbeille",
  finance_category_restored: "Catégorie de dépense restaurée",
  finance_category_purged: "Catégorie de dépense purgée de la corbeille",
  finance_subscription_deleted: "Abonnement interne mis à la corbeille",
  finance_subscription_restored: "Abonnement interne restauré",
  finance_subscription_purged: "Abonnement interne purgé de la corbeille",
  billing_offer_trash_deleted: "Offre mise à la corbeille",
  billing_offer_trash_restored: "Offre restaurée de la corbeille",
  billing_offer_trash_purged: "Offre purgée de la corbeille",
  interaction_deleted: "Échange mis à la corbeille",
  interaction_restored: "Échange restauré",
  interaction_purged: "Échange purgé de la corbeille",
  form_submission_deleted: "Réponse du formulaire mise à la corbeille",
  form_submission_restored: "Réponse du formulaire restaurée",
  form_submission_purged: "Réponse du formulaire purgée de la corbeille",
  assist_note_deleted: "Compte rendu d'assistance mis à la corbeille",
  assist_note_restored: "Compte rendu d'assistance restauré",
  assist_note_purged: "Compte rendu d'assistance purgé de la corbeille",
  records_document_deleted: "Brouillon de document supprimé",
  collaborator_deleted: "Fiche collaborateur mise à la corbeille",
  collaborator_restored: "Fiche collaborateur restaurée",
  collaborator_purged: "Fiche collaborateur purgée de la corbeille",
  service_deleted: "Service mis à la corbeille",
  service_restored: "Service restauré",
  service_purged: "Service purgé de la corbeille",
  org_referential_deleted: "Zone ou type de relation mis à la corbeille",
  org_referential_restored: "Zone ou type de relation restauré",
  org_referential_purged: "Zone ou type de relation purgé de la corbeille",
  skill_deleted: "Compétence mise à la corbeille",
  skill_restored: "Compétence restaurée",
  skill_purged: "Compétence purgée de la corbeille",
  job_function_deleted: "Fonction métier mise à la corbeille",
  job_function_restored: "Fonction métier restaurée",
  job_function_purged: "Fonction métier purgée de la corbeille",
  role_restored: "Rôle restauré de la corbeille",
  role_purged: "Rôle purgé de la corbeille",
  procurement_supplier_deleted: "Fournisseur mis à la corbeille",
  procurement_supplier_restored: "Fournisseur restauré",
  procurement_supplier_purged: "Fournisseur purgé de la corbeille",
  procurement_request_deleted: "Demande d'achat supprimée",
  mission_type_deleted: "Type de mission mis à la corbeille",
  mission_type_restored: "Type de mission restauré",
  mission_type_purged: "Type de mission purgé de la corbeille",
  procedure_template_deleted: "Modèle de procédure mis à la corbeille",
  procedure_template_restored: "Modèle de procédure restauré",
  procedure_template_purged: "Modèle de procédure purgé de la corbeille",
  report_template_deleted: "Modèle de compte rendu mis à la corbeille",
  report_template_restored: "Modèle de compte rendu restauré",
  report_template_purged: "Modèle de compte rendu purgé de la corbeille",
  training_program_deleted: "Programme de formation mis à la corbeille",
  training_program_restored: "Programme de formation restauré",
  training_program_purged: "Programme de formation purgé de la corbeille",
  mission_deleted: "Mission mise à la corbeille",
  mission_restored: "Mission restaurée",
  mission_purged: "Mission purgée de la corbeille",
  training_session_deleted: "Session de formation mise à la corbeille",
  training_session_restored: "Session de formation restaurée",
  training_session_purged: "Session de formation purgée de la corbeille",
  equipment_asset_deleted: "Équipement mis à la corbeille",
  equipment_asset_restored: "Équipement restauré",
  equipment_asset_purged: "Équipement purgé de la corbeille",
  finance_expense_deleted: "Brouillon de dépense supprimé",
  finance_claim_deleted: "Brouillon de note de frais supprimé",
  marketing_campaign_deleted: "Campagne mise à la corbeille",
  marketing_campaign_restored: "Campagne restaurée",
  marketing_campaign_purged: "Campagne purgée de la corbeille",
  marketing_partner_deleted: "Partenaire mis à la corbeille",
  marketing_partner_restored: "Partenaire restauré",
  marketing_partner_purged: "Partenaire purgé de la corbeille",
  governance_decision_deleted: "Décision mise à la corbeille",
  governance_decision_restored: "Décision restaurée",
  governance_decision_purged: "Décision purgée de la corbeille",
  governance_meeting_deleted: "Réunion mise à la corbeille",
  governance_meeting_restored: "Réunion restaurée",
  governance_meeting_purged: "Réunion purgée de la corbeille",
  governance_risk_deleted: "Risque mis à la corbeille",
  governance_risk_restored: "Risque restauré",
  governance_risk_purged: "Risque purgé de la corbeille",
  knowledge_article_deleted: "Article mis à la corbeille",
  knowledge_article_restored: "Article restauré",
  knowledge_article_purged: "Article purgé de la corbeille",
  product_item_deleted: "Retour produit mis à la corbeille",
  product_item_restored: "Retour produit restauré",
  product_item_purged: "Retour produit purgé de la corbeille",
  product_release_deleted: "Version mise à la corbeille",
  product_release_restored: "Version restaurée",
  product_release_purged: "Version purgée de la corbeille",
  product_incident_deleted: "Incident mis à la corbeille",
  product_incident_restored: "Incident restauré",
  product_incident_purged: "Incident purgé de la corbeille",
  ticket_created: "Ticket ouvert",
  ticket_deleted: "Ticket mis à la corbeille",
  ticket_restored: "Ticket restauré",
  ticket_purged: "Ticket purgé de la corbeille",
  ticket_updated: "Ticket modifié",
  ticket_assigned: "Responsable du ticket changé",
  ticket_status_changed: "Statut du ticket changé",
  ticket_priority_adjusted: "Priorité du ticket ajustée",
  ticket_priority_lifted: "Ajustement de priorité levé",
  ticket_entry_added: "Échange ou note ajouté au ticket",
  ticket_linked: "Élément lié au ticket",
  ticket_unlinked: "Lien retiré du ticket",
  ticket_satisfaction_recorded: "Satisfaction du ticket notée",
  ticket_pharmacy_attached: "Ticket rattaché à une pharmacie",
  tickets_exported: "Tickets exportés",
  billing_offer_created: "Offre créée",
  billing_offer_updated: "Offre modifiée",
  billing_offer_archived: "Offre archivée",
  billing_offer_restored: "Offre remise en vente",
  billing_offer_draft_created: "Nouvelle version d'offre commencée",
  billing_offer_draft_saved: "Brouillon d'offre enregistré",
  billing_offer_draft_discarded: "Brouillon d'offre supprimé",
  billing_offer_published: "Version d'offre publiée",
  billing_subscription_created: "Abonnement client créé",
  billing_subscription_updated: "Abonnement client modifié",
  billing_subscription_started: "Abonnement client démarré",
  billing_subscription_billing_started: "Facturation de l'abonnement démarrée",
  billing_subscription_paused: "Abonnement client mis en pause",
  billing_subscription_resumed: "Abonnement client repris",
  billing_subscription_ended: "Abonnement client terminé",
  billing_subscription_cancelled: "Abonnement client annulé",
  billing_subscription_offer_changed: "Offre de l'abonnement changée",
  billing_installment_cancelled: "Échéance annulée",
  billing_reminder_recorded: "Relance consignée",
  billing_invoice_recorded: "Facture consignée",
  billing_invoice_cancelled: "Facture annulée",
  billing_credit_note_recorded: "Avoir consigné",
  billing_credit_note_cancelled: "Avoir annulé",
  billing_payment_recorded: "Encaissement consigné",
  billing_payment_cancelled: "Encaissement annulé",
  billing_payment_refunded: "Remboursement consigné",
  records_document_created: "Document enregistré",
  records_obligation_created: "Obligation créée",
  records_obligation_updated: "Obligation modifiée",
  records_obligation_done: "Obligation faite",
  records_obligation_cancelled: "Obligation annulée",
  records_obligation_task_linked: "Tâche reliée à une obligation",
  records_constitution_item_created: "Pièce de constitution ajoutée",
  records_constitution_item_updated: "Pièce de constitution modifiée",
  records_template_created: "Modèle de document créé",
  records_template_updated: "Modèle de document modifié",
  records_template_draft_saved: "Brouillon de modèle enregistré",
  records_template_draft_started: "Nouvelle version de modèle",
  records_template_published: "Modèle de document publié",
  records_template_draft_discarded: "Brouillon de modèle abandonné",
  records_brand_updated: "Charte graphique des documents modifiée",
  records_brand_asset_added: "Image ajoutée à la médiathèque des documents",
  records_brand_asset_retired: "Image retirée de la médiathèque des documents",
  records_clause_created: "Clause créée",
  records_clause_updated: "Clause modifiée",
  records_clause_draft_saved: "Brouillon de clause enregistré",
  records_clause_draft_started: "Nouvelle version de clause",
  records_clause_published: "Clause publiée",
  records_clause_draft_discarded: "Brouillon de clause abandonné",
  records_clause_theme_updated: "Thème de clause modifié",
  records_document_updated: "Document modifié",
  records_version_added: "Version de document ajoutée",
  records_document_submitted: "Document soumis en revue",
  records_document_approved: "Document approuvé",
  records_document_returned: "Document renvoyé en brouillon",
  records_document_signed: "Signature de document consignée",
  records_document_terminated: "Document résilié",
  records_document_archived: "Document archivé",
  records_document_abandoned: "Document abandonné",
  records_access_changed: "Accès au document modifié",
  records_type_updated: "Type de document modifié",
  records_exported: "Registre des documents exporté",
  billing_exported: "Revenus exportés",
  training_session_reopened: "Session de formation rouverte",
  training_session_cancelled: "Session de formation annulée",
  training_sessions_exported: "Export des sessions de formation téléchargé",
  equipment_asset_created: "Équipement ajouté",
  equipment_asset_updated: "Équipement modifié",
  equipment_asset_retired: "Équipement retiré",
  equipment_check_recorded: "Équipement contrôlé",
  equipment_check_corrected: "Contrôle de matériel corrigé",
  equipment_issue_opened: "Anomalie matérielle ouverte",
  equipment_issue_updated: "Anomalie matérielle modifiée",
  equipment_issue_started: "Anomalie matérielle prise en charge",
  equipment_issue_resolved: "Anomalie matérielle résolue",
  equipment_issue_abandoned: "Anomalie matérielle abandonnée",
  equipment_issue_reopened: "Anomalie matérielle rouverte",
  equipment_issue_follow_up_linked: "Tâche de suivi liée à une anomalie",
  equipment_issues_exported: "Export des anomalies téléchargé",
  finance_claim_created: "Note de frais créée",
  finance_claim_corrected: "Note de frais corrigée",
  finance_claim_submitted: "Note de frais envoyée pour approbation",
  finance_claim_approved: "Note de frais approuvée",
  finance_claim_rejected: "Note de frais refusée",
  finance_claim_returned: "Note de frais renvoyée à corriger",
  finance_category_cap_set: "Plafond de catégorie modifié",
  finance_advance_given: "Avance de mission remise",
  finance_advance_returned: "Restitution d'avance enregistrée",
  finance_advance_cancelled: "Avance de mission annulée",
  finance_inbox_added: "Justificatifs déposés à saisir",
  finance_inbox_discarded: "Justificatif à saisir retiré",
  finance_import_committed: "Import de dépenses validé",
  finance_import_cancelled: "Import de dépenses annulé",
  finance_export_downloaded: "Export Finance téléchargé",
  // WhatsApp (compte de notification)
  whatsapp_test_send: "Message WhatsApp de test",
  whatsapp_login_started: "Connexion d'un compte WhatsApp lancée",
  whatsapp_logged_out: "Compte WhatsApp déconnecté",
  // Jamora Assist
  assist_request_accepted: "Demande d'assistance prise en charge",
  assist_session_joined: "Console d'assistance ouverte",
  assist_session_co_joined: "Assistance d'un collègue rejointe",
  assist_session_co_left: "Assistance d'un collègue quittée",
  assist_session_ended: "Assistance terminée",
  assist_access_denied: "Accès à une assistance refusé",
  assist_note_updated: "Compte rendu d'assistance modifié",
  mission_type_created: "Type de mission créé",
  mission_type_updated: "Type de mission modifié",
  mission_created: "Mission créée",
  mission_updated: "Mission modifiée",
  mission_planned: "Mission planifiée",
  mission_started: "Mission démarrée",
  mission_completed: "Mission terminée",
  mission_postponed: "Mission reportée",
  mission_cancelled: "Mission annulée",
  mission_interrupted: "Mission interrompue",
  mission_member_added: "Intervenant ajouté à une mission",
  mission_member_removed: "Intervenant retiré d'une mission",
  mission_lead_changed: "Responsable de mission changé",
  // Copilote Jamora
  assistant_key_updated: "Clé API du Copilote enregistrée",
  assistant_key_removed: "Clé API du Copilote supprimée",
  assistant_connection_tested: "Connexion du Copilote testée",
  assistant_access_changed: "Accès au Copilote modifié",
  assistant_usage_exported: "Consommation du Copilote exportée",
  assistant_conversation_viewed: "Échange du Copilote consulté",
};

export const RESOURCE_TYPE_LABELS = {
  automation_rule: "Automatisation",
  agent_token: "Jeton d'agent externe",
  mandate_type: "Type de mandat",
  finance_supplier_link: "Lien fournisseur",
  prospect: "Prospect",
  task: "Tâche",
  user: "Utilisateur",
  role: "Rôle",
  authentication: "Authentification",
  csv_import: "Import CSV",
  google_sheet_sync: "Synchronisation Google Sheet",
  google_sheet_config: "Configuration Google Sheet",
  permission: "Permission",
  assist_request: "Assistance à distance",
  pilot: "Pilote",
  setting: "Paramètre",
  assistant: "Copilote Jamora",
  whatsapp: "WhatsApp",
  collaborator: "Collaborateur",
  organisation: "Organisation",
  service: "Service",
  org_referential: "Organisation",
  skill: "Compétence",
  skill_level: "Niveau de compétence",
  job_function: "Fonction métier",
  record: "Document",
  governance_decision: "Décision",
  governance_meeting: "Réunion",
  governance_risk: "Risque",
  knowledge_article: "Article",
  governance_export: "Export de la gouvernance",
  cockpit_export: "Export du Cockpit",
  architecture: "Cartographie du système",
  jamora_admin: "Jamora App",
  marketing_campaign: "Campagne",
  marketing_partner: "Partenaire",
  marketing_touch: "Origine d'une pharmacie",
  marketing_preferences: "Préférences de communication",
  marketing_export: "Export du marketing",
  procurement_supplier: "Fournisseur",
  procurement_asset: "Actif",
  procurement_request: "Demande d'achat",
  procurement_inventory: "Inventaire",
  procurement_export: "Export des achats",
  record_type: "Type de document",
  record_obligation: "Obligation",
  record_constitution_item: "Pièce de constitution",
  record_template: "Modèle de document",
  interaction: "Échange",
  form_submission: "Réponse du formulaire",
  assist_note: "Compte rendu d'assistance",
  records_clause: "Clause de document",
  procedure_template: "Modèle de procédure",
  record_brand: "Charte graphique des documents",
  record_clause: "Clause de document",
  record_export: "Export du registre",
  finance_expense: "Dépense",
  finance_attachment: "Justificatif",
  finance_category: "Catégorie de dépense",
  finance_settings: "Paramètres Finance",
  finance_decision: "Décision sur des avances",
  finance_reimbursement: "Remboursement",
  finance_advance: "Avance de mission",
  finance_subscription: "Abonnement",
  mission: "Mission",
  mission_type: "Type de mission",
  private_file: "Fichier",
  procedure: "Procédure",
  checklist: "Procédure de mission",
  report_template: "Modèle de compte rendu",
  attendance: "Temps de mission",
  equipment_asset: "Équipement",
  equipment_issue: "Anomalie matérielle",
  equipment_category: "Catégorie de matériel",
  jamora_feature: "Fonctionnalité Jamora",
  jamora_feature_section: "Rubrique des fonctionnalités Jamora",
  training_module: "Module de formation",
  training_program: "Programme de formation",
  training_session: "Session de formation",
  customer: "Dossier client",
  ticket: "Ticket support",
  product_item: "Retour produit",
  product_release: "Version livrée",
  product_incident: "Incident produit",
  product_export: "Export produit",
  billing_offer: "Offre",
  billing_subscription: "Abonnement client",
  billing_installment: "Échéance",
  billing_document: "Facture ou avoir",
  billing_payment: "Encaissement",
  billing_export: "Export des revenus",
  finance_import: "Import de dépenses",
  finance_export: "Export Finance",
};

export const OUTCOME_LABELS = {
  success: "Réussi",
  failure: "Échec",
  denied: "Refusé",
};

// One color family per resource type (same "bg-X-50 text-X-700" pill
// convention as lib/roles.js's roleBadgeClass), so a resource is recognizable
// at a glance in the journal table.
export const RESOURCE_TYPE_BADGE_CLASS = {
  prospect: "bg-blue-50 text-blue-700",
  task: "bg-indigo-50 text-indigo-700",
  user: "bg-cyan-50 text-cyan-700",
  role: "bg-violet-50 text-violet-700",
  authentication: "bg-rose-50 text-rose-700",
  csv_import: "bg-amber-50 text-amber-700",
  google_sheet_sync: "bg-teal-50 text-teal-700",
  google_sheet_config: "bg-teal-50 text-teal-700",
  permission: "bg-orange-50 text-orange-700",
  assist_request: "bg-sky-50 text-sky-700",
};

export const OUTCOME_BADGE_CLASS = {
  success: "bg-emerald-50 text-emerald-700",
  failure: "bg-red-50 text-red-700",
  denied: "bg-red-50 text-red-700",
};

// Same field set as ProspectDetail.jsx's INFO_FIELD_GROUPS (the ProspectUpdate
// allowlist), duplicated here as plain labels so this module doesn't import a
// page component; keep the two in sync if a field is renamed.
const PROSPECT_FIELD_LABELS = {
  pharmacy_name: "Nom de la pharmacie",
  address: "Adresse",
  region: "Région",
  commune: "Commune",
  phone: "Téléphone",
  phone2: "Téléphone secondaire",
  email: "Email",
  whatsapp: "WhatsApp",
  pharmacist_name: "Pharmacien titulaire",
  pharmacist_license: "Numéro de licence",
  pharmacist_phone: "Téléphone du titulaire",
  pharmacist_email: "Email du titulaire",
  reference_name: "Nom du référent",
  reference_function: "Fonction du référent",
  reference_phone: "Téléphone du référent",
  opening_hours: "Horaires d'ouverture",
  on_call_duty: "Gardes",
  closed_days: "Jours de fermeture",
  hours_notes: "Précisions horaires",
  current_software: "Logiciel actuel",
  desired_start: "Démarrage souhaité",
  acquisition_channel: "Canal d'acquisition",
};

const TASK_FIELD_LABELS = {
  title: "Titre",
  description: "Description",
  due_date: "Échéance",
  assigned_to: "Responsable",
  priority: "Priorité",
  task_type: "Type",
  status: "Statut",
};

function prettifySlug(slug) {
  if (!slug) return slug;
  const spaced = slug.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function changedFieldsSummary(changedFields, fieldLabels) {
  const keys = Object.keys(changedFields || {});
  if (keys.length === 0) return null;
  return keys.map((k) => fieldLabels[k] || prettifySlug(k)).join(", ");
}

/** One-line, human summary for an activity event, used by both the
 * per-prospect timeline and the global admin journal. `usersById` (optional)
 * maps id -> user, used to prefer a live name over the frozen snapshot. */
export function describeActivityEvent(event, { usersById } = {}) {
  const meta = event.metadata || {};
  switch (event.event_type) {
    case "status_changed":
      return meta.from && meta.to ? `${STAGE_LABEL[meta.from] || meta.from} → ${STAGE_LABEL[meta.to] || meta.to}` : null;
    case "prospect_updated":
      return changedFieldsSummary(meta.changed_fields, PROSPECT_FIELD_LABELS);
    case "task_updated":
      return changedFieldsSummary(meta.changed_fields, TASK_FIELD_LABELS);
    case "user_role_changed":
      return meta.from && meta.to ? `${prettifySlug(meta.from)} → ${prettifySlug(meta.to)}` : null;
    case "user_login_failed":
      return meta.email ? `Email : ${meta.email}` : null;
    case "permission_denied":
      return meta.path ? `${meta.method || ""} ${meta.path}`.trim() : null;
    case "csv_import_completed":
      return `${meta.created ?? 0} créé(s), ${meta.skipped_existing ?? 0} déjà existant(s), ${meta.ambiguous ?? 0} ambigu(s)`;
    case "google_sheet_sync_completed":
      return `${meta.prospects_created ?? 0} créé(s), ${meta.prospects_attached ?? 0} rattaché(s)`;
    case "google_sheet_config_updated": {
      const parts = [];
      if (meta.csv_url_changed) parts.push("URL modifiée");
      if (meta.auto_sync) parts.push(`Sync auto : ${meta.auto_sync.from ? "activée" : "désactivée"} → ${meta.auto_sync.to ? "activée" : "désactivée"}`);
      return parts.join(" · ") || null;
    }
    case "role_updated": {
      const parts = [];
      if (meta.permissions_added?.length) parts.push(`+${meta.permissions_added.length} permission(s)`);
      if (meta.permissions_removed?.length) parts.push(`-${meta.permissions_removed.length} permission(s)`);
      return parts.join(" · ") || null;
    }
    default:
      return null;
  }
}

// An event with no actor is either the app itself (a background sync) or an
// unauthenticated visitor (a failed login attempt); those read very
// differently in a journal, so they're never collapsed into one label.
const UNAUTHENTICATED_EVENT_TYPES = ["user_login_failed"];

// An action proposed by the Copilote Jamora and approved by the person (assistant/actions): the server marks the event
// (`metadata.via`) and the records it writes for itself (an interaction: `via`). The person remains the author.
export const VIA_COPILOT_EXPLANATION = "Proposée par le Copilote Jamora, approuvée et lancée par cette personne";

export function isViaCopilot(event) {
  return event?.metadata?.via === "assistant" || event?.via === "assistant";
}

export function actorIsSystem(event) {
  return !event.actor_id && !UNAUTHENTICATED_EVENT_TYPES.includes(event.event_type);
}

export function actorDisplayName(event, usersById) {
  if (!event.actor_id) {
    return UNAUTHENTICATED_EVENT_TYPES.includes(event.event_type) ? "Inconnu" : "Système";
  }
  const live = usersById?.[event.actor_id];
  if (live) return live.name;
  return event.actor_snapshot?.name || event.actor_snapshot?.email || "Utilisateur inconnu";
}
